import { useForm, useSelector } from '@tanstack/react-form';
import { shallow } from '@tanstack/react-store';
import { isOneOf, templateTypeSchema } from '@tronche2tek/shared-types';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ErrorMessage } from '../common/ErrorMessage';

/**
 * A name is the only thing the API requires; everything else is optional and
 * sent as `undefined` when left blank. Trimming happens in the schema so the
 * submit handler receives values already in the shape the API expects.
 */
const createTemplateSchema = z.object({
  name: z.string().trim().min(1),
  description: z
    .string()
    .trim()
    .transform((v) => v || undefined),
  type: z.enum(['student', 'employee', 'other']),
  frontSideId: z.string().transform((v) => v || undefined),
  backSideId: z.string().transform((v) => v || undefined),
});

export type CreateTemplateValues = z.output<typeof createTemplateSchema>;
type CreateTemplateFields = z.input<typeof createTemplateSchema>;

/**
 * Typed explicitly: `useForm` derives its field paths from what this returns,
 * and a bare literal would infer `type` as `string` rather than the enum.
 */
function initialCreateTemplateFields(): CreateTemplateFields {
  return {
    name: '',
    description: '',
    type: 'other',
    frontSideId: '',
    backSideId: '',
  };
}

interface TemplateCreateFormProps {
  sides: { id: string; name: string }[];
  onCreate: (values: CreateTemplateValues) => Promise<void>;
}

/** Create-template form. Owns its fields, saving state and error display. */
export function TemplateCreateForm({
  sides,
  onCreate,
}: TemplateCreateFormProps) {
  const { t } = useTranslation();
  const typeId = useId();
  const frontId = useId();
  const backId = useId();
  // The API rejection is form-level, not tied to a field, so it lives beside
  // the form rather than in it.
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: initialCreateTemplateFields(),
    // Zod 4 implements Standard Schema, so the schema is the validator — no
    // resolver package in between. It also normalises on the way out: trimmed
    // strings, and blanks as `undefined`.
    validators: { onSubmit: createTemplateSchema },
    onSubmit: async ({ value }) => {
      setSubmitError(null);
      try {
        await onCreate(createTemplateSchema.parse(value));
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : String(err));
      }
    },
  });

  // The button mirrors the schema's own `name` requirement. Selected with a
  // shallow compare: these two flip a handful of times per session, so the
  // occasional whole-form render beats a footer render on every keystroke
  // (a fresh-object Subscribe selector fails the store's `===` every event).
  const { isSubmitting, nameEntered } = useSelector(
    form.store,
    (state) => ({
      isSubmitting: state.isSubmitting,
      nameEntered: state.values.name.trim().length > 0,
    }),
    { compare: shallow },
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-3 border border-rule bg-blue/4 p-5"
    >
      <h4 className="text-sm font-semibold text-ink">
        {t('admin.templates.newTemplate')}
      </h4>
      {sides.length === 0 && (
        <p className="border border-coral/35 bg-coral/10 px-3 py-2 text-xs text-coral">
          {t('admin.templates.noSidesWarning')}
        </p>
      )}
      <form.Field name="name">
        {(field) => (
          <input
            required
            placeholder={t('admin.templates.templateNamePlaceholder')}
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            className="w-full px-3 py-2 text-sm"
          />
        )}
      </form.Field>
      <form.Field name="description">
        {(field) => (
          <input
            placeholder={t('admin.templates.descriptionPlaceholder')}
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            className="w-full px-3 py-2 text-sm"
          />
        )}
      </form.Field>
      <div>
        <label htmlFor={typeId} className="mb-1 block text-xs text-muted">
          {t('admin.templates.typeLabel')}
        </label>
        <form.Field name="type">
          {(field) => (
            <select
              id={typeId}
              value={field.state.value}
              onChange={(e) => {
                const { value } = e.target;
                if (isOneOf(templateTypeSchema.options, value)) {
                  field.handleChange(value);
                }
              }}
              onBlur={field.handleBlur}
              className="w-full p-2 text-sm"
            >
              {(['student', 'employee', 'other'] as const).map((tp) => (
                <option key={tp} value={tp}>
                  {t(`admin.templates.type.${tp}`)}
                </option>
              ))}
            </select>
          )}
        </form.Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={frontId} className="mb-1 block text-xs text-muted">
            {t('admin.templates.frontLabel')}
          </label>
          <form.Field name="frontSideId">
            {(field) => (
              <select
                id={frontId}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className="w-full p-2 text-sm"
              >
                <option value="">{t('admin.templates.noneOption')}</option>
                {sides.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </form.Field>
        </div>
        <div>
          <label htmlFor={backId} className="mb-1 block text-xs text-muted">
            {t('admin.templates.backLabel')}
          </label>
          <form.Field name="backSideId">
            {(field) => (
              <select
                id={backId}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className="w-full p-2 text-sm"
              >
                <option value="">{t('admin.templates.noneOption')}</option>
                {sides.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </form.Field>
        </div>
      </div>
      {submitError && <ErrorMessage message={submitError} size="xs" />}
      <button
        type="submit"
        disabled={isSubmitting || !nameEntered}
        className="btn-blue w-full justify-center"
        style={{ opacity: isSubmitting || !nameEntered ? 0.4 : 1 }}
      >
        {isSubmitting
          ? t('admin.templates.saving')
          : t('admin.templates.saveTemplate')}
      </button>
    </form>
  );
}
